import { createPortal } from 'react-dom';
import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import { useTheme } from '../../hooks/useTheme';
import type { ThemeMode } from '../../hooks/useTheme';
import { useUserStore } from '../../stores/useUserStore';
import { OnboardVideos } from '../../config/cdn-assets';
import styles from './WelcomeModal.module.css';

interface WelcomeModalProps {
  onClose: () => void;
}

// --- Icons ---

const MoonIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const SunIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const AutoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const ChatIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const ImageIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const VideoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

const ToolsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

// --- Data ---

const themeIcons: Record<ThemeMode, React.ReactNode> = {
  dark: <MoonIcon />,
  light: <SunIcon />,
  auto: <AutoIcon />,
};

const featureIcons = [
  { icon: <ChatIcon />, key: 'askAI', hint: 'ChatGPT / Claude / Gemini', path: '/chat' },
  { icon: <ImageIcon />, key: 'createImage', hint: 'Nano / Recraft / Seedream', path: '/image' },
  { icon: <VideoIcon />, key: 'createVideo', hint: 'Kling / Veo 3.1 / Sora 2', path: '/video' },
  { icon: <ToolsIcon />, key: 'aiTools', hintKey: 'motionAndMore', path: '/tools' },
];

// --- Component ---

const WelcomeModal = ({ onClose }: WelcomeModalProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { haptic } = useTelegramWebApp();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const userData = useUserStore(s => s.user);

  const isLightVideo = theme === 'light' || (theme === 'auto' && resolvedTheme === 'light');
  const hasSubscription = !!(userData?.subscriptionTier && userData.subscriptionTier !== 'free');

  // Автозакрытие для подписчиков
  useEffect(() => {
    if (hasSubscription) onClose();
  }, [hasSubscription]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const dismiss = useCallback(() => {
    haptic.impact('light');
    onClose();
  }, [haptic, onClose]);

  const handleBackdropClick = (e: React.MouseEvent | React.TouchEvent) => {
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
      dismiss();
    }
  };

  const handleThemeSelect = (value: ThemeMode) => {
    haptic.impact('light');
    setTheme(value);
  };

  const handleFeature = (path: string) => {
    haptic.impact('light');
    onClose();
    navigate(path);
  };

  const handleClose = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dismiss();
  };

  if (hasSubscription) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      onTouchEnd={handleBackdropClick}
    >
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        {/* Video + theme selector */}
        <div className={styles.videoBlock}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            src={OnboardVideos.light}
            autoPlay
            muted
            loop
            playsInline
            disablePictureInPicture
            disableRemotePlayback
            controlsList="nodownload nofullscreen noplaybackrate"
            tabIndex={-1}
            aria-hidden="true"
            className={`${styles.dayVideo} ${isLightVideo ? styles.dayVideoVisible : ''}`}
          />
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            src={OnboardVideos.night}
            autoPlay
            muted
            loop
            playsInline
            disablePictureInPicture
            disableRemotePlayback
            controlsList="nodownload nofullscreen noplaybackrate"
            tabIndex={-1}
            aria-hidden="true"
            className={`${styles.nightVideo} ${!isLightVideo ? styles.nightVideoVisible : ''}`}
          />
          <div className={styles.themeSelector}>
            {(['dark', 'light', 'auto'] as ThemeMode[]).map((value) => (
              <button
                key={value}
                type="button"
                className={`${styles.themeOption} ${theme === value ? styles.active : ''}`}
                onClick={() => handleThemeSelect(value)}
              >
                <span className={styles.themeIcon}>{themeIcons[value]}</span>
                <span className={styles.themeLabel}>{t(`theme.${value}`)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.contentBg} aria-hidden="true" />
          <h2 className={styles.title}>{t('welcome.title')}</h2>
          <p className={styles.subtitle}>{t('welcome.subtitle')}</p>

          {/* Features */}
          <div className={styles.featuresGrid}>
            {featureIcons.map((f) => {
              const text = t(`welcome.${f.key}`);
              const hint = f.hintKey ? t(`welcome.${f.hintKey}`) : f.hint;
              return (
                <button
                  key={f.path}
                  type="button"
                  className={styles.featureCard}
                  onClick={() => handleFeature(f.path)}
                  aria-label={`${text}: ${hint}`}
                  title={`${text}: ${hint}`}
                >
                  <div className={styles.featureContent}>
                    <div className={styles.featureText}>{text}</div>
                    <div className={styles.featureDescription}>{hint}</div>
                  </div>
                  <div className={styles.featureIcon} aria-hidden="true">
                    {f.icon}
                  </div>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className={styles.startButton}
            onClick={handleClose}
            onTouchEnd={handleClose}
          >
            <span className={styles.startButtonText}>{t('btn.startFree')}</span>
          </button>

          <div className={styles.legalLinks}>
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className={styles.legalLink}>
              {t('welcome.privacy')}
            </a>
            <span className={styles.legalDivider}>—</span>
            <a href="/public-offer" target="_blank" rel="noopener noreferrer" className={styles.legalLink}>
              {t('welcome.offer')}
            </a>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WelcomeModal;
